import type { ActionKind, ActionOption, GameView } from "@coup/shared";
import { useEffect } from "react";
import { RoleChip } from "../../components/RoleChip";
import { ACTION_LABEL, type Names } from "./names";
import styles from "./Table.module.css";

const CLAIMS: Partial<Record<ActionKind, "duke" | "ambassador" | "captain" | "assassin">> = {
  tax: "duke",
  exchange: "ambassador",
  steal: "captain",
  assassinate: "assassin",
};

function detail(o: ActionOption, keep: number): string {
  if (!o.enabled) return o.reason ?? "";
  switch (o.kind) {
    case "income":
      return "+1 coin · cannot be stopped";
    case "foreignAid":
      return "+2 coins · any Duke may block";
    case "tax":
      return "+3 coins";
    case "exchange":
      return `Draw 2, keep ${keep}, return the rest`;
    case "steal":
      return "Take up to 2 coins";
    case "assassinate":
      return "Pay 3 · they lose a card";
    case "coup":
      return "Pay 7 · cannot be stopped";
  }
}

interface Props {
  view: GameView;
  names: Names;
  /** "treasury", "deck" or a target player id. */
  menu: string;
  x: number;
  y: number;
  onPick: (o: ActionOption) => void;
  onClose: () => void;
}

export function ActionMenu({ view, names, menu, x, y, onPick, onClose }: Props) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (view.options.t !== "declare") return null;
  const actions = view.options.actions;
  const items =
    menu === "treasury"
      ? actions.filter((o) => ["income", "foreignAid", "tax"].includes(o.kind))
      : menu === "deck"
        ? actions.filter((o) => o.kind === "exchange")
        : actions.filter((o) => o.target === menu);
  const title =
    menu === "treasury"
      ? "Treasury"
      : menu === "deck"
        ? "Court deck"
        : `Act on ${names.name(menu)}`;

  return (
    <div className={styles.menu} style={{ left: x, top: y }} role="menu">
      <div className={styles.menuHead}>
        <div className={styles.menuTitle}>{title}</div>
        <button
          type="button"
          className={styles.menuClose}
          aria-label="Close menu"
          onClick={onClose}
        >
          ×
        </button>
      </div>
      {items.map((o) => {
        const claim = CLAIMS[o.kind];
        return (
          <button
            key={o.kind}
            type="button"
            role="menuitem"
            className={styles.menuItem}
            disabled={!o.enabled}
            onClick={() => onPick(o)}
          >
            <div className={styles.menuItemTop}>
              <span className={styles.menuLabel}>{ACTION_LABEL[o.kind]}</span>
              <span style={{ flex: 1 }} />
              {claim && <RoleChip role={claim} prefix="Claim " />}
            </div>
            <span className={styles.menuDetail}>{detail(o, view.hand.length)}</span>
          </button>
        );
      })}
    </div>
  );
}
